import React from 'react';
import { Box, Typography } from '@mui/material';
import usePermissions from '../../utils/usePermissions';
import { HOME_WIDGETS, WidgetDefinition } from './widgets/registry';
import { HomeNavigate, HomeNavigationContext } from './widgets/HomeNavigationContext';

interface HomePageProps {
  targetInterface?: 'admin' | 'dashboard';
  /** Переход в раздел панели (для виджетов со ссылками) */
  onNavigate?: HomeNavigate;
}

/** Минимальная высота верхнего блока на широком экране (правая колонка не должна схлопываться) */
const MIN_TOP_HEIGHT = 480;

/** Высота блока на узком экране, где колонки идут друг под другом: избыток прокручивается внутри */
const MOBILE_BLOCK_HEIGHT = 420;

/** Потолок высоты окон нижнего ряда (под первым экраном): ниже - высота по содержимому, выше - скролл внутри */
const BOTTOM_BLOCK_HEIGHT = 340;

/**
 * Страница «Главная»: первый экран - две колонки по макету: слева широкая (выручка, входящие и последние документы),
 * справа узкая (уведомления, статусы). Под ним - нижний ряд из двух окон (требующие внимания, акции), страница
 * прокручивается вниз. Виджет без права не рендерится, колонка без виджетов схлопывается, оставшаяся занимает
 * всю ширину. В блоках с большим числом строк скролл внутри блока.
 */
const HomePage: React.FC<HomePageProps> = ({ targetInterface = 'admin', onNavigate }) => {
  const { hasPerm } = usePermissions();
  const widgets = HOME_WIDGETS.filter(w => w.interfaces.includes(targetInterface) && hasPerm(w.perm));
  const left = widgets.filter(w => w.column === 'left');
  const right = widgets.filter(w => w.column === 'right');
  const bottom = widgets.filter(w => w.column === 'bottom');

  if (widgets.length === 0) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography sx={{ fontSize: 14, color: '#94a3b8' }}>Для вашей роли нет доступных виджетов</Typography>
      </Box>
    );
  }

  /**
   * pinned - колонка не влияет на высоту первого экрана: растягивается по соседней (абсолютное позиционирование),
   * длинный список внутри скроллится. Высоту экрана задаёт левая колонка по содержимому блоков.
   */
  const renderColumn = (list: WidgetDefinition[], pinned: boolean) => (
    <Box sx={{ position: 'relative', minWidth: 0, minHeight: 0 }}>
      <Box
        sx={{
          display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0,
          position: { xs: 'static', lg: pinned ? 'absolute' : 'static' },
          inset: { lg: pinned ? 0 : undefined },
          height: { xs: 'auto', lg: pinned ? 'auto' : '100%' }, minHeight: 0
        }}
      >
        {list.map(w => {
          const Widget = w.component;
          const auto = w.grow === 'auto';
          const fit = w.grow === 'fit';
          const content = auto || fit;
          return (
            <Box
              key={w.id}
              sx={{
                minWidth: 0,
                display: 'flex', flexDirection: 'column',
                minHeight: content ? 0 : { xs: MOBILE_BLOCK_HEIGHT, lg: 120 },
                maxHeight: fit ? { xs: MOBILE_BLOCK_HEIGHT, lg: w.maxHeight ?? 300 } : undefined,
                height: content ? 'auto' : { xs: MOBILE_BLOCK_HEIGHT, lg: 'auto' },
                flex: { xs: '0 0 auto', lg: auto ? '0 0 auto' : fit ? '0 1 auto' : `${w.grow} 1 0` }
              }}
            >
              <Widget />
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  const columns = [left, right].filter(c => c.length > 0);
  const template = columns.length === 2 ? 'minmax(0, 2fr) minmax(0, 1fr)' : 'minmax(0, 1fr)';
  const bottomTemplate = bottom.length > 1 ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)';

  return (
    <HomeNavigationContext.Provider value={onNavigate ?? null}>
      <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {columns.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: template },
              gap: 2,
              // Высоту задаёт левая колонка (блоки по содержимому), правая подстраивается; нижняя граница - чтобы правая не схлопывалась
              minHeight: { lg: MIN_TOP_HEIGHT }
            }}
          >
            {columns.map((c, i) => (
              <React.Fragment key={i}>{renderColumn(c, columns.length === 2 && c === right)}</React.Fragment>
            ))}
          </Box>
        )}

        {bottom.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: bottomTemplate },
              gap: 2,
              // Высота строки - по самому высокому окну (по содержимому), но не больше BOTTOM_BLOCK_HEIGHT; избыток записей прокручивается внутри
              gridAutoRows: 'auto',
              alignItems: 'stretch'
            }}
          >
            {bottom.map(w => {
              const Widget = w.component;
              return (
                <Box key={w.id} sx={{ minWidth: 0, minHeight: 0, maxHeight: BOTTOM_BLOCK_HEIGHT, display: 'flex', flexDirection: 'column' }}>
                  <Widget />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </HomeNavigationContext.Provider>
  );
};

export default HomePage;
